import { useCallback, useEffect, useRef, useState } from 'react';
import {
  availabilityOverridesApi,
  businessHoursApi,
  servicesApi,
  type AvailabilityOverride,
  type BusinessHour,
} from '@/services/api';
import { mergeBusinessHours } from '@/utils/availability';
import type { Service } from '@/types/Service';

/**
 * Loads business-tab data per agent (parallel fetch).
 * Hours and services are stored for every agent. Availability overrides
 * are CRM-only. Lives in AgentPage so local edits survive tab switches;
 * call `refetch` when the Business tab is opened to pick up external changes.
 */
export function useAgentBusinessData(
  agentId: number | undefined,
  options?: { includeAvailabilityOverrides?: boolean }
) {
  const includeAvailabilityOverrides =
    options?.includeAvailabilityOverrides ?? true;
  const [businessHours, setBusinessHours] = useState<BusinessHour[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [availabilityOverrides, setAvailabilityOverrides] = useState<
    AvailabilityOverride[]
  >([]);
  const [isBusinessHoursLoading, setIsBusinessHoursLoading] = useState(() =>
    Boolean(agentId)
  );
  const [isServicesLoading, setIsServicesLoading] = useState(() =>
    Boolean(agentId)
  );
  const [isOverridesLoading, setIsOverridesLoading] = useState(
    () => Boolean(agentId) && includeAvailabilityOverrides
  );
  const [reloadToken, setReloadToken] = useState(0);
  const previousAgentIdRef = useRef<number | undefined>(undefined);

  const refetch = useCallback(() => {
    setReloadToken(token => token + 1);
  }, []);

  useEffect(() => {
    if (!agentId) {
      previousAgentIdRef.current = undefined;
      setBusinessHours([]);
      setServices([]);
      setAvailabilityOverrides([]);
      setIsBusinessHoursLoading(false);
      setIsServicesLoading(false);
      setIsOverridesLoading(false);
      return;
    }

    const agentChanged = previousAgentIdRef.current !== agentId;
    previousAgentIdRef.current = agentId;

    let cancelled = false;
    if (agentChanged) {
      setBusinessHours([]);
      setServices([]);
      setAvailabilityOverrides([]);
    }
    setIsBusinessHoursLoading(true);
    setIsServicesLoading(true);
    setIsOverridesLoading(includeAvailabilityOverrides);

    void (async () => {
      const hoursPromise = businessHoursApi.fetchBusinessHours(agentId);
      const servicesPromise = servicesApi.fetchServices(agentId);
      const overridesPromise = includeAvailabilityOverrides
        ? availabilityOverridesApi.fetchByAgent(agentId)
        : Promise.resolve([] as AvailabilityOverride[]);

      const [hoursResult, servicesResult, overridesResult] =
        await Promise.allSettled([
          hoursPromise,
          servicesPromise,
          overridesPromise,
        ]);

      if (cancelled) return;

      if (hoursResult.status === 'fulfilled') {
        setBusinessHours(hoursResult.value);
      } else {
        console.error('Failed to load business hours:', hoursResult.reason);
        setBusinessHours(mergeBusinessHours(agentId, []));
      }

      if (servicesResult.status === 'fulfilled') {
        setServices(servicesResult.value);
      } else {
        console.error('Failed to load services:', servicesResult.reason);
        setServices([]);
      }

      if (includeAvailabilityOverrides) {
        if (overridesResult.status === 'fulfilled') {
          setAvailabilityOverrides(overridesResult.value);
        } else {
          console.error(
            'Failed to load availability overrides:',
            overridesResult.reason
          );
          setAvailabilityOverrides([]);
        }
      } else {
        setAvailabilityOverrides([]);
      }

      setIsBusinessHoursLoading(false);
      setIsServicesLoading(false);
      setIsOverridesLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [agentId, includeAvailabilityOverrides, reloadToken]);

  return {
    businessHours,
    setBusinessHours,
    services,
    setServices,
    availabilityOverrides,
    setAvailabilityOverrides,
    isBusinessHoursLoading,
    isServicesLoading,
    isOverridesLoading,
    refetch,
  };
}
